import React, { useState } from "react";
import { Container, Row, Col, Button, ButtonGroup } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FaBolt, FaInfoCircle } from "react-icons/fa";
import { plans } from "../utils/static";
import {
  PLAN_PRICING,
  calculatePlanPrice,
  validateApiCallsForPlan,
  getPlanPricingConfig,
  formatPlanDisplayPrice,
} from "../utils/planPricing";
import ApiCallsSelector from "./shared/ApiCallsSelector";
import PlanApiAccessList from "./shared/PlanApiAccessList";
import { formatPlanApiCallsLabel } from "../utils/planApiAccess";
import { toast } from "react-toastify";

const buildInitialApiCalls = () => {
  const initial = {};
  plans.forEach((plan) => {
    if (plan.isFree) return;
    const cfg = getPlanPricingConfig(plan.backendName);
    initial[plan.backendName] = cfg?.minCalls ?? plan.apiLimit;
  });
  return initial;
};

const PricingArea = ({ priceExcludesGst = false }) => {
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [currency, setCurrency] = useState("INR");
  const [apiCallsByPlan, setApiCallsByPlan] = useState(buildInitialApiCalls);
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector((state) => state.auth);

  const updatePlanApiCalls = (backendName, value) => {
    setApiCallsByPlan((prev) => ({ ...prev, [backendName]: value }));
  };

  const getPlanApiCalls = (plan) => apiCallsByPlan[plan.backendName];

  const getPlanPrice = (plan) => {
    if (!plan.pricing || !plan.pricing[currency]) return 0;
    return plan.pricing[currency][billingCycle] || 0;
  };

  const getPlanAvailability = (plan) => {
    if (plan.isFree) {
      return { valid: true, apiCalls: PLAN_PRICING.FREE.includedCalls };
    }
    return validateApiCallsForPlan(plan.backendName, getPlanApiCalls(plan));
  };

  const displayPrice = (plan) => {
    if (plan.isFree) return 0;
    const availability = getPlanAvailability(plan);
    if (!availability.valid) return null;
    try {
      const result = calculatePlanPrice(plan.backendName, availability.apiCalls, {
        interval: billingCycle,
        currency,
      });
      if (priceExcludesGst) {
        return currency === "USD" ? result.subtotalUSD : Math.round(result.subtotalINR);
      }
      return currency === "USD" ? result.subtotalUSD : Math.round(result.amount);
    } catch {
      return getPlanPrice(plan);
    }
  };

  const displayApiLimit = (plan) => {
    if (plan.isFree) return plan.apiLimit;
    const availability = getPlanAvailability(plan);
    if (availability.valid) return availability.apiCalls;
    return getPlanPricingConfig(plan.backendName)?.includedCalls ?? plan.apiLimit;
  };

  const getApiCallsLabel = (plan) => {
    const availability = getPlanAvailability(plan);
    if (!plan.isFree && !availability.valid) return null;
    return formatPlanApiCallsLabel(
      plan.backendName,
      plan.isFree ? 100 : availability.apiCalls
    );
  };

  const handleChoosePlan = (plan) => {
    if (!plan.isFree) {
      const validation = validateApiCallsForPlan(
        plan.backendName,
        getPlanApiCalls(plan)
      );
      if (!validation.valid) {
        toast.error(validation.error);
        return;
      }
    }

    const calls = plan.isFree
      ? PLAN_PRICING.FREE.includedCalls
      : validateApiCallsForPlan(plan.backendName, getPlanApiCalls(plan)).apiCalls;

    if (!isAuthenticated) {
      localStorage.setItem(
        "selectedPlan",
        JSON.stringify({
          planName: plan.backendName,
          billingCycle,
          currency,
          customApiRequests: calls,
        })
      );
      toast.info("Please register to continue with your plan selection.");
      navigate("/register");
    } else {
      navigate("/dashboard/subscription", {
        state: {
          selectedPlan: plan.backendName,
          billingCycle,
          currency,
          autoSelect: true,
          customApiRequests: calls,
        },
      });
    }
  };

  return (
    <>
      <div className="pricing sp">
        <Container>
          <Row>
            <Col lg={8} className="m-auto text-center">
              <div className="heading1">
                <span
                  className="span"
                  data-aos="zoom-in-left"
                  data-aos-duration="700"
                >
                  <img src="/assets/img/icons/span1.png" alt="" /> Our Pricing
                  Plan
                </span>
                <h2 className="title tg-element-title">
                  Explore Our Flexible Pricing Plans
                </h2>
              </div>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col className="text-center">
              <div className="d-flex justify-content-center gap-3 mb-3">
                <ButtonGroup>
                  <Button
                    variant={
                      billingCycle === "monthly" ? "primary" : "outline-primary"
                    }
                    onClick={() => setBillingCycle("monthly")}
                    style={{ padding: "8px 24px", fontWeight: "500" }}
                  >
                    Monthly
                  </Button>
                  <Button
                    variant={
                      billingCycle === "yearly" ? "primary" : "outline-primary"
                    }
                    onClick={() => setBillingCycle("yearly")}
                    style={{ padding: "8px 24px", fontWeight: "500" }}
                  >
                    Yearly
                  </Button>
                </ButtonGroup>

                <ButtonGroup>
                  <Button
                    variant={currency === "USD" ? "primary" : "outline-primary"}
                    onClick={() => setCurrency("USD")}
                    style={{ padding: "8px 24px", fontWeight: "500" }}
                  >
                    USD
                  </Button>
                  <Button
                    variant={currency === "INR" ? "primary" : "outline-primary"}
                    onClick={() => setCurrency("INR")}
                    style={{ padding: "8px 24px", fontWeight: "500" }}
                  >
                    INR
                  </Button>
                </ButtonGroup>
              </div>

              {billingCycle === "yearly" && (
                <div className="text-success">
                  <small>
                    <FaBolt className="me-1" />
                    Save up to 10% with yearly billing
                  </small>
                </div>
              )}
            </Col>
          </Row>

          <div className="space30"></div>
          <Row className="g-4">
            {plans.map((plan, index) => {
              const IconComponent = plan.icon;
              const availability = getPlanAvailability(plan);
              const finalPrice = displayPrice(plan);
              const isWidePlan = index >= 3;
              const colLg = isWidePlan ? 6 : 4;
              const colMd = isWidePlan ? 6 : 6;

              return (
                <Col
                  lg={colLg}
                  md={colMd}
                  sm={12}
                  key={plan.name}
                  data-aos="fade-up"
                  data-aos-duration={700 + index * 200}
                >
                  <div
                    className={`single-pricing-box h-100 position-relative ${
                      plan.popular ? "active" : ""
                    }`}
                    style={{
                      minHeight: isWidePlan ? "720px" : "680px",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {plan.popular && (
                      <div className="most-popular">
                        <p>Most Popular</p>
                      </div>
                    )}

                    <div
                      className="text-center mb-3"
                      style={{
                        background: plan.gradient,
                        color: "white",
                        padding: "20px 15px",
                        borderRadius: "8px 8px 0 0",
                        margin: "-20px -20px 20px -20px",
                      }}
                    >
                      <IconComponent size={28} className="mb-2" />
                      <p
                        className="title text-white fw-bold mb-1"
                        style={{ color: "white !important" }}
                      >
                        {plan.displayName}
                      </p>
                      <p className="small mb-0 opacity-90">{plan.subtitle}</p>
                    </div>

                    <div className="text-center mb-3">
                      <h2 className="fw-bold" style={{ color: "#007bff" }}>
                        {plan.isFree ? (
                          "Free"
                        ) : !availability.valid ? (
                          <span
                            className="fs-5 text-muted"
                            style={{ color: "#6c757d !important" }}
                          >
                            —
                          </span>
                        ) : (
                          <>
                            {currency === "USD" ? "$" : "₹"}
                            {formatPlanDisplayPrice(finalPrice, currency)}
                            <span
                              style={{ fontSize: "16px", color: "#6c757d" }}
                            >
                              /{billingCycle === "yearly" ? "year" : "month"}
                            </span>
                          </>
                        )}
                      </h2>

                      {!plan.isFree && availability.valid && currency === "INR" && (
                        <div className="small text-muted">
                          {priceExcludesGst ? "+ 18% GST" : "incl. 18% GST"}
                        </div>
                      )}

                      {plan.isFree && (
                        <div
                          className="mt-2 small fw-bold"
                          style={{ color: "#10b981" }}
                        >
                          <FaInfoCircle className="me-2" />
                          No card required to activate Free Plan
                        </div>
                      )}

                      {billingCycle === "yearly" &&
                        availability.valid &&
                        !plan.isFree && (
                          <div className="small text-success">
                            <FaBolt className="me-1" />
                            Save 10% with yearly billing
                          </div>
                        )}
                    </div>

                    <div className="text-center mb-3">
                      <div className="badge bg-light text-dark border px-3 py-2">
                        {plan.unlimited
                          ? "🔥 Unlimited API Calls"
                          : availability.valid
                            ? `🚀 ${displayApiLimit(plan)?.toLocaleString()} API calls/month`
                            : "🚀 Enter valid API calls"}
                      </div>
                    </div>

                    <div className="border"></div>

                    {!plan.isFree && (
                      <ApiCallsSelector
                        compact
                        billingCycle={billingCycle}
                        selectedPlanBackendName={plan.backendName}
                        value={getPlanApiCalls(plan)}
                        onChange={(value) =>
                          updatePlanApiCalls(plan.backendName, value)
                        }
                      />
                    )}

                    <div style={{ flex: "1" }}>
                      <PlanApiAccessList
                        planBackendName={plan.backendName}
                        apiCallsLabel={getApiCallsLabel(plan)}
                      />
                    </div>

                    <div className="space30"></div>
                    <div className="button" style={{ marginTop: "auto" }}>
                      <Button
                        className="theme-btn1 w-100"
                        onClick={() => handleChoosePlan(plan)}
                        variant="primary"
                        size="lg"
                        disabled={!plan.isFree && !availability.valid}
                        style={{
                          background:
                            !plan.isFree && !availability.valid
                              ? "#6c757d"
                              : "linear-gradient(45deg, #007bff, #0056b3)",
                          border: "none",
                          padding: "12px 20px",
                          borderRadius: "5px",
                          fontWeight: "bold",
                        }}
                      >
                        {!plan.isFree && !availability.valid
                          ? "Enter valid API calls"
                          : "Choose Plan"}
                        <span>
                          <i className="fa-solid fa-arrow-right ms-2"></i>
                        </span>
                      </Button>
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        </Container>
      </div>
    </>
  );
};

export default PricingArea;
